import { memo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Page as PDFPage } from 'react-pdf';
import Box from '@mui/material/Box';

import { isFinancialDocumentSelector, isTwoWayMatchOnSelector } from '@/domain/documents';
import { isStatementSelector } from '@/domain/journal/selectors';
import DocumentPageControls from '@/pages/document/components/DocumentPageControls';
import { useDocumentZoom, useDocumentZoomButtons } from '@/pages/document/DocumentZoom';
import type { PdfPageBox } from '@/pages/document/DocumentViewer/DocumentFileView/pageGeometry';
import type { RotateDirection } from '@/pages/document/DocumentViewer/DocumentFileView/hooks/useUserRotations';
import ApprovalStampPlacement from '@/pages/document/DocumentViewer/DocumentFileView/PageView/ApprovalStampPlacement';
import DocumentStatusBadge from '@/pages/document/DocumentViewer/DocumentFileView/PageView/components/DocumentStatusBadge';
import {
  DocumentStatusBadgeAnchor,
  PageArea,
  PageWrapper,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/components/StyledComponents';
import PageOcrLayer from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer';
import { OcrWordsSelectionGestureContext } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageOcrLayer/OcrWords/ocrWordsSelectionGesture';
import { PageTwoWayMatchLayer } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer';
import { turnFromUpright } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/twoWayMatchGeometry';
import {
  PAGE_PAN_X,
  usePageGestures,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/usePageGestures';

// The payment page reads them from here.
export { BORDER_WIDTH, MAX_DOC_WIDTH } from '@/pages/document/DocumentViewer/DocumentFileView/pageGeometry';

type Props = {
  pageIndex: number;
  pageCount: number;
  documentId: string;
  /** The width the page is drawn at, before the zoom. */
  pageWidth: number;
  isPaid: boolean;
  isPaymentInProgress: boolean;
  userRotation: number;
  displayedRotation: number;
  zoomedHeight: number;
  onPageDrawn: (pageIndex: number) => void;
  onRotate: (page: number, direction: RotateDirection) => void;
  onGridShownChange: (isShown: boolean) => void;
  scrollElement: HTMLElement | null;
  isTwoWayMatchLayerShown: boolean;
  recognizedRotation: number;
  pdfPageBox: PdfPageBox;
};

const PAN_TRANSFORM = `translate3d(var(${PAGE_PAN_X}, 0px), 0, 0)`;

const PageView = ({
  pageIndex,
  pageCount,
  documentId,
  pageWidth,
  isPaid,
  isPaymentInProgress,
  userRotation,
  displayedRotation,
  zoomedHeight,
  onPageDrawn,
  onRotate,
  onGridShownChange,
  scrollElement,
  isTwoWayMatchLayerShown,
  recognizedRotation,
  pdfPageBox,
}: Props) => {
  const isFinancial: boolean = useSelector(isFinancialDocumentSelector);
  const isTwoWayMatchOn: boolean = useSelector(isTwoWayMatchOnSelector);
  const isStatement: boolean = useSelector(isStatementSelector);
  const scale = useDocumentZoom();
  const { onZoom, onReset, getIsDisabledZoomButton } = useDocumentZoomButtons();
  const { rootRef, pageRef, isSelecting, gridStartY, registerSelectionGesture, pageHandlers } = usePageGestures({
    scrollElement,
  });
  const [isStampDragging, setIsStampDragging] = useState(false);
  const [grid, setGrid] = useState<{ startY: number } | null>(null);

  // A page that is no longer a statement loses its grid.
  if (grid && !isStatement) setGrid(null);

  const toggleGrid = useCallback(() => {
    const next = grid ? null : { startY: gridStartY() };
    onGridShownChange(next !== null);
    setGrid(next);
  }, [grid, gridStartY, onGridShownChange]);

  const onRenderSuccess = useCallback(() => onPageDrawn(pageIndex), [onPageDrawn, pageIndex]);

  const shownWidth = Math.ceil(pageWidth * scale);
  const shownHeight = Math.ceil(zoomedHeight);
  const showStatusBadge = isPaid || isPaymentInProgress;
  const isShowButtons = !grid && !isSelecting && !isStampDragging;

  return (
    <Box ref={rootRef} sx={{ position: 'relative' }}>
      {showStatusBadge && (
        <DocumentStatusBadgeAnchor width={shownWidth} style={{ transform: `${PAN_TRANSFORM} translateY(-100%)` }}>
          <DocumentStatusBadge variant={isPaid ? 'paid' : 'paymentPreparation'} />
        </DocumentStatusBadgeAnchor>
      )}
      <PageWrapper>
        <PageArea
          ref={pageRef}
          id={`pdfPage-${pageIndex + 1}`}
          role="presentation"
          paid={isPaid}
          paymentInProgress={isPaymentInProgress}
          style={{ width: shownWidth, height: shownHeight, transform: PAN_TRANSFORM }}
          {...pageHandlers}
        >
          <PDFPage
            renderTextLayer={false}
            width={pageWidth}
            onRenderSuccess={onRenderSuccess}
            rotate={displayedRotation}
            scale={scale}
            renderAnnotationLayer
            pageNumber={pageIndex + 1}
          />
          <OcrWordsSelectionGestureContext.Provider value={registerSelectionGesture}>
            <PageOcrLayer
              documentId={documentId}
              pageNumber={pageIndex + 1}
              width={pageWidth}
              height={zoomedHeight}
              scale={scale}
              userRotation={userRotation}
              showGrid={grid !== null}
              gridStartY={grid?.startY ?? 0}
              onCloseGrid={toggleGrid}
            />
          </OcrWordsSelectionGestureContext.Provider>
          {isTwoWayMatchLayerShown && (
            <PageTwoWayMatchLayer
              width={pageWidth}
              height={zoomedHeight}
              scale={scale}
              page={pageIndex + 1}
              userRotation={userRotation}
              turnFromUpright={turnFromUpright(userRotation, recognizedRotation)}
            />
          )}
          {pageIndex === 0 && (
            <ApprovalStampPlacement
              pageWidth={shownWidth}
              pageHeight={shownHeight}
              pdfPageBox={pdfPageBox}
              userRotation={userRotation}
              onDraggingChange={setIsStampDragging}
            />
          )}
        </PageArea>
      </PageWrapper>
      {isShowButtons && (
        <DocumentPageControls
          total={pageCount}
          currentNumber={pageIndex + 1}
          isStatement={isStatement}
          handleGrid={toggleGrid}
          onRotateLeft={() => onRotate(pageIndex, 'left')}
          onRotateRight={() => onRotate(pageIndex, 'right')}
          isSmallActionButton={pageWidth <= 420}
          onZoom={onZoom}
          onReset={onReset}
          getIsDisabledZoomButton={getIsDisabledZoomButton}
          isRotateDisabled={isTwoWayMatchOn && isFinancial}
          pageWidth={pageWidth}
        />
      )}
    </Box>
  );
};

export default memo(PageView);
